import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import supportService from '../services/api/supportService';
import type { ListMyRequestsParams } from '../services/api/supportService';
import type { CreateSupportRequestInput, SupportReplyInput, SupportRequestDetail } from '@upskill/shared';
import { useAuthStore } from '../stores/authStore';

const DEFAULT_PAGE_SIZE = 20;

// Namespaced apart, NOT nested under a shared 'support' prefix each other's a
// child of: invalidating REQUESTS_KEY must refetch only the list, never the
// unread-count poll or an open thread's detail (which `setQueryData` right
// after a mutation would otherwise have its own write immediately clobbered
// by a refetch that invalidation triggered).
const REQUESTS_KEY = ['support', 'requests'] as const;
const UNREAD_COUNT_KEY = ['support', 'unread-count'] as const;
const requestDetailKey = (id: string) => ['support', 'request', id] as const;

/**
 * "Load more" pagination (not prev/next controls — the widget's 384px panel
 * is too narrow for page-number UI and this is a chat-style list). Default
 * page size 20 — the architect's call, chosen so a normal user's own request
 * list realistically never needs a second page in practice while still
 * bounding the query.
 */
export function useMySupportRequests(params: Omit<ListMyRequestsParams, 'page'> = {}) {
  const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);
  const limit = params.limit ?? DEFAULT_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [...REQUESTS_KEY, { ...params, limit }],
    queryFn: ({ pageParam }) => supportService.getMyRequests({ ...params, limit, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined),
    staleTime: 30_000,
    enabled: isReady,
    retry: false,
  });
}

export function useSupportRequest(id: string | null | undefined) {
  const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);

  return useQuery({
    queryKey: id ? requestDetailKey(id) : ['support', 'request', 'none'],
    queryFn: () => supportService.getRequestDetail(id as string),
    enabled: isReady && !!id,
    retry: false,
  });
}

/** Launcher badge — polled every 15s, matches useNotifications' useUnreadCount. */
export function useSupportUnreadCount() {
  const isReady = useAuthStore((s) => s.isAuthenticated && !!s.accessToken);

  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: () => supportService.getUnreadCount(),
    refetchInterval: isReady ? 15_000 : false,
    staleTime: 10_000,
    enabled: isReady,
    retry: false,
  });
}

export function useCreateSupportRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSupportRequestInput) => supportService.createSupportRequest(input),
    onSuccess: (created: SupportRequestDetail) => {
      queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      queryClient.setQueryData(requestDetailKey(created.id), created);
    },
  });
}

export function useSupportReply(requestId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SupportReplyInput) => {
      if (!requestId) throw new Error('useSupportReply: requestId is required');
      return supportService.replyToRequest(requestId, input);
    },
    onSuccess: (updated: SupportRequestDetail) => {
      queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      queryClient.setQueryData(requestDetailKey(updated.id), updated);
    },
  });
}

export function useMarkSupportRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => supportService.markRequestRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });
}

export function useCsat(requestId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rating: number) => {
      if (!requestId) throw new Error('useCsat: requestId is required');
      return supportService.rateCsat(requestId, rating);
    },
    onSuccess: (updated: SupportRequestDetail) => {
      queryClient.setQueryData(requestDetailKey(updated.id), updated);
    },
  });
}
